import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { styled } from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectIsLoading,
  selectLoadingMessage,
  setIsLoading,
  setLoadingMessage,
  setUploadedFile,
  setUploadedFileName,
  setConversationId,
  setConversationNo,
  clearResults,
  selectIsConversationOpen,
  setIsConversationOpen,
  setReportFilePath
} from '../../store/features/transmissionSlice';
import { chatWithTransmission, fetchFailedReportTransmission, generateConversationId } from '../../store/services/transmissionService';
import axios from 'axios';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const BASE_URL = "https://eudtuapimgmd01.azure-api.net/transmission-failure/api/v1/";

interface TransmissionUploadModalProps {
}

const TransmissionUploadModal: React.FC<TransmissionUploadModalProps> = () => {
  const dispatch = useDispatch();
  const isLoading = useSelector(selectIsLoading);
  const loadingMessage = useSelector(selectLoadingMessage);
  const state = useSelector((state: any) => state);
  const [isOpen, setIsOpen] = useState(true);
  const isConversationOpen = useSelector(selectIsConversationOpen);
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Helper function to get headers for axios
  const getHeaders = (isFileUpload = false) => {
    const headers: Record<string, string> = {
      'Ocp-Apim-Subscription-Key': '949f1556f4cf4d969014bece6dcb2005'
    }

    // if (!isFileUpload) {
    //   headers['Content-Type'] = 'application/json'
    // }

    if (state.auth.idToken) {
      headers['Authorization'] = `Bearer ${state.auth.idToken}`
    }

    return headers
  }

  // File upload function using axios
  const uploadFileWithAxios = async (file: File) => {
    dispatch(setIsLoading(true))
    dispatch(setLoadingMessage("Uploading file..."))
    setError(null)

    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await axios.post(`${BASE_URL}upload`, formData, {
        headers: getHeaders(true)
      })

      if (response.status !== 200) {
        throw new Error(`Upload failed: ${response.status} ${response.statusText}`)
      }

      dispatch(setLoadingMessage("File uploaded successfully"))
      dispatch(setReportFilePath(response.data.blob_path))
      return response.data
    } catch (err: any) {
      console.error("Error uploading file:", err)
      const errorMessage = err.message || "Upload failed. Please try again."
      setError(errorMessage)
      dispatch(setLoadingMessage("Upload failed"))
      throw err
    } finally {
      dispatch(setIsLoading(false))
    }
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      // Convert File object to serializable FileMetadata
      const fileMetadata = {
        name: selectedFile.name,
        size: selectedFile.size,
        type: selectedFile.type,
        lastModified: selectedFile.lastModified
      };

      dispatch(setUploadedFile(fileMetadata));

      // Generate new conversation ID
      const newConversationId = generateConversationId();
      dispatch(setConversationId(newConversationId));

      // Reset conversation number to 0
      dispatch(setConversationNo(0));

      // Upload file
      const response = await uploadFileWithAxios(selectedFile);
      dispatch(setUploadedFileName(response.blob_path));
      // Clear previous results
      dispatch(clearResults());

      // Call chatWithTransmission with conversation number 0 and empty user_prompt
      await fetchFailedReportTransmission({
        user_prompt: "",
        conversation_no: 0,
        conversation_id: newConversationId,
        test_report: response.blob_path.replace('.csv', ''),
        user_email: state.auth.email || state.auth.userId
      }, state, dispatch);

      // If we reach here, everything was successful
      handleClose();
      dispatch(setIsConversationOpen(false));

    } catch (error: any) {
      console.error('Upload or chat failed:', error);
      // Don't close the modal, error is already set by uploadFileWithAxios
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setError(null);
    dispatch(setIsLoading(false));
    dispatch(setLoadingMessage(""));
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Button variant="outlined" onClick={() => {
          window.open("/transmission", "_blank");
          return
        }}>
          Upload New Report
        </Button>
      </div>
      <Dialog
        open={isConversationOpen}
        onClose={(event, reason) => {
          if (reason === 'backdropClick') {
            return; // Prevent closing on backdrop click
          }
          handleClose();
        }}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle>
          Upload Transmission Data File
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Please select a CSV file containing transmission data for analysis.
            </Typography>

            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                disabled={isLoading}
              >
                Select CSV File
                <VisuallyHiddenInput
                  type="file"
                  onChange={handleFileSelect}
                  accept=".csv"
                />
              </Button>

              {selectedFile && (
                <Typography variant="body2" color="text.secondary">
                  {selectedFile.name}
                </Typography>
              )}
            </Box>

            {isLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">
                  {loadingMessage}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={!selectedFile || isLoading}
          >
            Upload & Analyze
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TransmissionUploadModal; 